import React, { useRef, useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import Webcam from "react-webcam";
import { Loader, Text, useMantineTheme } from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";
import { isMobile } from "react-device-detect";
import useDocumentDetection from "../../../hooks/useDocumentDetection";
import useLocale from "../../../hooks/useLocale";
import { ReactComponent as VeratadLogo } from "../../../assets/veratad_ai_white_logo_large.svg";

const STYLE_ID = "veratad-doc-capture-styles";

function injectStyles(glowColor) {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    :root { --doc-glow-color: ${glowColor}; }

    @keyframes docGlowPulse {
      0%, 100% {
        box-shadow:
          0 0 calc(6px * var(--doc-glow-intensity)) calc(2px * var(--doc-glow-intensity)) var(--doc-glow-color),
          0 0 calc(18px * var(--doc-glow-intensity)) calc(5px * var(--doc-glow-intensity)) var(--doc-glow-color);
        opacity: calc(0.3 + 0.14 * var(--doc-glow-intensity));
      }
      50% {
        box-shadow:
          0 0 calc(12px * var(--doc-glow-intensity)) calc(4px * var(--doc-glow-intensity)) var(--doc-glow-color),
          0 0 calc(30px * var(--doc-glow-intensity)) calc(10px * var(--doc-glow-intensity)) var(--doc-glow-color);
        opacity: calc(0.4 + 0.12 * var(--doc-glow-intensity));
      }
    }

    @keyframes docGlowLocked {
      0% {
        box-shadow:
          0 0 calc(14px * var(--doc-glow-intensity)) calc(4px * var(--doc-glow-intensity)) var(--doc-glow-color),
          0 0 calc(35px * var(--doc-glow-intensity)) calc(10px * var(--doc-glow-intensity)) var(--doc-glow-color);
      }
      100% {
        box-shadow:
          0 0 calc(10px * var(--doc-glow-intensity)) calc(3px * var(--doc-glow-intensity)) var(--doc-glow-color),
          0 0 calc(25px * var(--doc-glow-intensity)) calc(8px * var(--doc-glow-intensity)) var(--doc-glow-color);
      }
    }

    @keyframes badgeFadeIn {
      from { opacity: 0; transform: translateY(6px); }
      to { opacity: 1; transform: translateY(0); }
    }

    /* Hide native video play button / controls on mobile */
    video::-webkit-media-controls,
    video::-webkit-media-controls-panel,
    video::-webkit-media-controls-play-button,
    video::-webkit-media-controls-start-playback-button {
      display: none !important;
      -webkit-appearance: none !important;
    }
  `;
  document.head.appendChild(style);
}

function removeStyles() {
  const el = document.getElementById(STYLE_ID);
  if (el) el.remove();
}

// Corner bracket SVG component
function CornerBrackets({ x, y, width, height, color, detected }) {
  const bracketLen = 24;
  const strokeWidth = 3;
  const opacity = detected ? 1 : 0.4;
  const c = detected ? color : "rgba(255,255,255,0.5)";

  return (
    <g style={{ transition: "opacity 0.3s" }} opacity={opacity}>
      {/* Top-left */}
      <polyline
        points={`${x},${y + bracketLen} ${x},${y} ${x + bracketLen},${y}`}
        fill="none"
        stroke={c}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Top-right */}
      <polyline
        points={`${x + width - bracketLen},${y} ${x + width},${y} ${x + width},${y + bracketLen}`}
        fill="none"
        stroke={c}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Bottom-left */}
      <polyline
        points={`${x},${y + height - bracketLen} ${x},${y + height} ${x + bracketLen},${y + height}`}
        fill="none"
        stroke={c}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Bottom-right */}
      <polyline
        points={`${x + width - bracketLen},${y + height} ${x + width},${y + height} ${x + width},${y + height - bracketLen}`}
        fill="none"
        stroke={c}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
}

// Analyze a video element for passport indicators (MRZ + aspect ratio)
function analyzePassportFromVideo(video) {
  try {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;

    const aspectRatio = vw / vh;
    const aspectRatioPassport = aspectRatio >= 1.3 && aspectRatio <= 1.5;

    const canvas = document.createElement("canvas");
    canvas.width = vw;
    canvas.height = vh;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, vw, vh);

    // Scan bottom 30% for MRZ bands
    const mrzTop = Math.floor(vh * 0.7);
    const mrzHeight = vh - mrzTop;
    const imageData = ctx.getImageData(0, mrzTop, vw, mrzHeight);
    const pixels = imageData.data;

    const darkThreshold = 80;
    const minDarkPercent = 0.35;
    let denseRowCount = 0;
    let bandCount = 0;
    let inBand = false;

    for (let row = 0; row < mrzHeight; row++) {
      let darkPixels = 0;
      for (let col = 0; col < vw; col++) {
        const idx = (row * vw + col) * 4;
        const luma = pixels[idx] * 0.299 + pixels[idx + 1] * 0.587 + pixels[idx + 2] * 0.114;
        if (luma < darkThreshold) darkPixels++;
      }
      if (darkPixels / vw >= minDarkPercent) {
        if (!inBand) { bandCount++; inBand = true; }
        denseRowCount++;
      } else {
        inBand = false;
      }
    }

    const mrzDetected = bandCount >= 2 && denseRowCount >= 10;

    return {
      isPassport: mrzDetected || aspectRatioPassport,
      mrzDetected,
      aspectRatioPassport,
      aspectRatio: aspectRatio.toFixed(3),
      bandCount,
      denseRowCount,
    };
  } catch {
    return null;
  }
}

export default function DocumentCapture({
  onCapture,
  onError,
  onReady,
  onCameraAllowed,
  side = "front",
  captureSettings = {},
  paused = false,
}) {
  const theme = useMantineTheme();
  const locale = useLocale("document-capture");
  const { width: vw, height: vh } = useViewportSize();
  const webcamRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [passportInfo, setPassportInfo] = useState(null);

  const primaryColor =
    theme.colors[theme.primaryColor]?.[5] || theme.colors.blue[5];

  const {
    modelsLoaded,
    modelLoadError,
    gatingState,
    feedbackKey,
    isAllPassing,
    isTooDark,
    documentType,
    confidence,
    boundingBox,
    showManualCapture,
    setOnAutoCapture,
    getFrameChain,
    areaRatio,
    docPresenceDetail,
    yoloDetail,
    captureStrategy,
  } = useDocumentDetection(webcamRef, !paused && cameraReady, captureSettings, side);

  const feedbackMessage = locale?.feedback?.[feedbackKey] || feedbackKey;

  // Count passing gates for glow intensity
  const passingCount = Object.values(gatingState).filter(Boolean).length;

  // Document guide dimensions (ID card aspect ratio 1.586:1)
  const guideWidth = isMobile ? Math.min(vw * 0.85, 340) : 360;
  const guideHeight = guideWidth / 1.586;
  const guideX = (vw - guideWidth) / 2;
  const guideY = (vh - guideHeight) / 2 - (isMobile ? 60 : 30);
  const guideRadius = 12;

  // Inject/remove CSS
  useEffect(() => {
    injectStyles(primaryColor);
    return removeStyles;
  }, [primaryColor]);

  // Notify parent when models + camera are ready
  useEffect(() => {
    if (modelsLoaded && cameraReady && onReady) {
      onReady();
    }
  }, [modelsLoaded, cameraReady, onReady]);

  const handleCapture = useCallback(() => {
    if (!webcamRef.current) return;
    try {
      const video = webcamRef.current.video;
      if (!video) return;

      const vWidth = video.videoWidth;
      const vHeight = video.videoHeight;

      const frameChain = getFrameChain();
      if (vWidth > 0 && vHeight > 0) {
        // Capture the full video frame — bounding-box cropping caused
        // partial captures due to coordinate mismatches between the
        // YOLO 640x640 input space and actual video dimensions.
        const canvas = document.createElement("canvas");
        canvas.width = vWidth;
        canvas.height = vHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, vWidth, vHeight);
        const imageSrc = canvas.toDataURL("image/jpeg", 0.95);

        if (imageSrc && onCapture) {
          onCapture(imageSrc, documentType, confidence, frameChain);
        }
      } else {
        // Fallback: use react-webcam screenshot
        const imageSrc = webcamRef.current.getScreenshot();
        if (imageSrc && onCapture) {
          onCapture(imageSrc, documentType, confidence, frameChain);
        }
      }
    } catch (err) {
      if (onError)
        onError({
          type: "capture_error",
          message: err?.message || "Screenshot capture failed",
        });
    }
  }, [onCapture, onError, documentType, confidence, getFrameChain]);

  // Register auto-capture callback
  useEffect(() => {
    setOnAutoCapture(handleCapture);
  }, [handleCapture, setOnAutoCapture]);

  // Periodic passport analysis on live video (debug)
  useEffect(() => {
    if (!cameraReady) return;
    const interval = setInterval(() => {
      const video = webcamRef.current?.video;
      if (video && video.videoWidth > 0) {
        setPassportInfo(analyzePassportFromVideo(video));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [cameraReady]);

  const handleUserMedia = useCallback(() => {
    const video = webcamRef.current?.video;
    if (!video) return;
    if (onCameraAllowed) onCameraAllowed();
    const checkReady = () => {
      if (
        video.videoWidth > 0 &&
        video.videoHeight > 0 &&
        video.readyState >= 2
      ) {
        setCameraReady(true);
      } else {
        requestAnimationFrame(checkReady);
      }
    };
    requestAnimationFrame(checkReady);
  }, [onCameraAllowed]);

  const handleUserMediaError = useCallback(
    (err) => {
      if (onError)
        onError({
          type: "camera_error",
          name: err?.name || "UnknownError",
          message: err?.message || "Camera access failed",
        });
    },
    [onError]
  );

  // Document type badge text
  const classificationBadge = documentType
    ? locale?.classification?.[documentType] || documentType.replace("_", " ")
    : null;

  const portal = (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 9999,
        backgroundColor: "#000",
        overflow: "hidden",
      }}
    >
      {/* Loading indicator with debug status */}
      {(!cameraReady || !modelsLoaded) && (
        <div
          style={{
            position: "absolute",
            left: guideX,
            top: guideY,
            width: guideWidth,
            height: guideHeight,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            pointerEvents: "none",
            zIndex: 15,
          }}
        >
          <Loader size={36} color="white" />
          <Text
            fw={500}
            c="white"
            fz={14}
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}
          >
            {locale?.capture?.loadingModels || "Loading document scanner..."}
          </Text>
          <div
            style={{
              fontSize: 10,
              fontFamily: "monospace",
              color: "rgba(255,255,255,0.7)",
              textAlign: "center",
              lineHeight: 1.8,
              textShadow: "0 1px 3px rgba(0,0,0,0.9)",
            }}
          >
            <div>Camera: {cameraReady ? "\u2705" : "\u23F3 waiting..."}</div>
            <div>YOLO model: {modelsLoaded ? (modelLoadError ? "\u26A0\uFE0F skipped (using JS contours)" : "\u2705") : "\u23F3 loading..."}</div>
          </div>
        </div>
      )}

      {/* Webcam layer */}
      <Webcam
        ref={webcamRef}
        audio={false}
        screenshotFormat="image/jpeg"
        screenshotQuality={1.0}
        playsInline
        videoConstraints={{
          facingMode: { exact: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          aspectRatio: { ideal: 16 / 9 },
        }}
        onUserMedia={handleUserMedia}
        onUserMediaError={handleUserMediaError}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: cameraReady && modelsLoaded ? 1 : 0,
          transition: "opacity 0.3s ease-in",
        }}
      />

      {/* SVG overlay with rounded-rect cutout + corner brackets */}
      <svg
        width={vw}
        height={vh}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
        }}
      >
        <defs>
          <mask id="doc-rect-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={guideX}
              y={guideY}
              width={guideWidth}
              height={guideHeight}
              rx={guideRadius}
              ry={guideRadius}
              fill="black"
            />
          </mask>
        </defs>

        {/* Dark overlay with rect cutout */}
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.85)"
          mask="url(#doc-rect-mask)"
        />

        {/* Guide border */}
        <rect
          x={guideX}
          y={guideY}
          width={guideWidth}
          height={guideHeight}
          rx={guideRadius}
          ry={guideRadius}
          fill="none"
          stroke={
            passingCount === 0 ? "rgba(255,255,255,0.5)" : primaryColor
          }
          strokeWidth={isAllPassing ? 4 : passingCount >= 4 ? 3.5 : 2.5}
          style={{ transition: "stroke 0.4s, stroke-width 0.4s" }}
        />

        {/* Corner brackets */}
        <CornerBrackets
          x={guideX}
          y={guideY}
          width={guideWidth}
          height={guideHeight}
          color={primaryColor}
          detected={gatingState.documentPresent}
        />
      </svg>

      {/* Glow div */}
      {(() => {
        const glowIntensity = passingCount === 0 ? 1 : passingCount + 1;
        const glowColorValue =
          passingCount === 0 ? "rgba(255,255,255,0.4)" : primaryColor;
        return (
          <div
            style={{
              position: "absolute",
              left: guideX,
              top: guideY,
              width: guideWidth,
              height: guideHeight,
              borderRadius: guideRadius,
              background: "transparent",
              pointerEvents: "none",
              zIndex: 10,
              "--doc-glow-intensity": glowIntensity,
              "--doc-glow-color": glowColorValue,
              animation: isAllPassing
                ? "docGlowLocked 1.5s ease-in-out infinite alternate"
                : "docGlowPulse 2s ease-in-out infinite",
              transition: "opacity 0.4s",
            }}
          />
        );
      })()}

      {/* Document type badge */}
      {cameraReady && modelsLoaded && classificationBadge && (
        <div
          style={{
            position: "absolute",
            top: guideY - 44,
            left: 0,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 20,
            animation: "badgeFadeIn 0.4s ease-out",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(0,0,0,0.7)",
              color: "#fff",
              padding: "6px 14px",
              borderRadius: 20,
              fontSize: 13,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>{classificationBadge}</span>
            {documentType === "passport" && (
              <span
                style={{
                  fontSize: 11,
                  opacity: 0.8,
                  fontWeight: 400,
                }}
              >
                {locale?.classification?.frontOnly || "— Front side only"}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Feedback text */}
      <div
        style={{
          position: "absolute",
          top: guideY + guideHeight + 30,
          left: 0,
          width: "100%",
          textAlign: "center",
          pointerEvents: "none",
          zIndex: 20,
        }}
      >
        <span
          style={{
            color: "#fff",
            fontSize: isMobile ? 16 : 18,
            fontWeight: 500,
            textShadow: "0 1px 4px rgba(0,0,0,0.8)",
          }}
        >
          {cameraReady && modelsLoaded ? feedbackMessage : ""}
        </span>
      </div>

      {/* Manual capture button (fallback after 20s timeout) */}
      {cameraReady && modelsLoaded && showManualCapture && (
        <div
          style={{
            position: "absolute",
            bottom: isMobile ? 68 : 60,
            left: 0,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            zIndex: 20,
          }}
        >
          <button
            onClick={handleCapture}
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              border: "4px solid white",
              backgroundColor: "rgba(255,255,255,0.2)",
              cursor: "pointer",
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) =>
              (e.target.style.backgroundColor = "rgba(255,255,255,0.4)")
            }
            onMouseOut={(e) =>
              (e.target.style.backgroundColor = "rgba(255,255,255,0.2)")
            }
          />
        </div>
      )}

      {/* Debug gate status overlay */}
      {cameraReady && modelsLoaded && (
        <div
          style={{
            position: "absolute",
            bottom: isMobile ? 100 : 90,
            left: 8,
            right: 8,
            backgroundColor: "rgba(0,0,0,0.7)",
            color: "rgba(255,255,255,0.9)",
            padding: "6px 10px",
            borderRadius: 6,
            fontSize: 10,
            fontFamily: "monospace",
            pointerEvents: "none",
            zIndex: 25,
            lineHeight: 1.6,
            display: "flex",
            flexWrap: "wrap",
            gap: "2px 10px",
          }}
        >
          {Object.entries(gatingState).map(([key, val]) => (
            <span key={key}>
              {val ? "\u2705" : "\u274C"} {key}
            </span>
          ))}
          <span style={{ opacity: 0.7 }}>
            | area: {(areaRatio * 100).toFixed(1)}% | conf: {(confidence * 100).toFixed(0)}% | {documentType || "none"} | {feedbackKey} | strategy: {captureStrategy}
          </span>
          <span style={{ width: "100%", borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 3, marginTop: 2, color: modelLoadError ? "#ff6b6b" : "#69db7c" }}>
            MODEL: {modelLoadError ? `YOLO FAILED → JS contour fallback | ${modelLoadError.message}` : "YOLO active"}
          </span>
          {yoloDetail && (
            <span style={{ width: "100%", borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 3, marginTop: 2 }}>
              SRC: {yoloDetail.source} | detections: {yoloDetail.totalDetections} | tensor: {yoloDetail.dims}
              {yoloDetail.all.length > 0 && yoloDetail.all.map((d, i) => (
                <span key={i} style={{ display: "block", paddingLeft: 10, color: d.conf > 0.7 ? "#69db7c" : "#ffd43b" }}>
                  [{i}] {d.cls} {(d.conf * 100).toFixed(1)}% @ ({d.box.x},{d.box.y} {d.box.w}x{d.box.h})
                </span>
              ))}
              {yoloDetail.peaks && yoloDetail.peaks.length > 0 && (
                <span style={{ display: "block", paddingTop: 3, marginTop: 2, borderTop: "1px dashed rgba(255,255,255,0.15)" }}>
                  PEAK SCORES (best of 8400):
                  {yoloDetail.peaks.map((p, i) => (
                    <span key={i} style={{
                      display: "inline-block",
                      marginLeft: 6,
                      color: p.peak >= 0.5 ? "#69db7c" : p.peak >= 0.2 ? "#ffd43b" : p.peak >= 0.05 ? "#ff922b" : "#ff6b6b",
                    }}>
                      {p.cls}: {(p.peak * 100).toFixed(1)}%
                    </span>
                  ))}
                </span>
              )}
            </span>
          )}
          {docPresenceDetail && (
            <span style={{ width: "100%", borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 3, marginTop: 2 }}>
              {docPresenceDetail.detected ? "\u2705" : "\u274C"} contour
              {docPresenceDetail.contourFound ? (
                <>
                  {" | verts: "}{docPresenceDetail.vertices}
                  {" | area: "}{(docPresenceDetail.contourAreaRatio * 100).toFixed(1)}%
                  {" | aspect: "}{docPresenceDetail.aspectRatio?.toFixed(2)}
                  {" (1.2-1.75)"}
                </>
              ) : (
                <>{" | no rectangle ("}{docPresenceDetail.contoursScanned ?? 0}{" scanned)"}</>
              )}
              {" | edges: "}{docPresenceDetail.edgePercent ?? "?"}%
              {" | contours: "}{docPresenceDetail.contoursScanned ?? 0}
              {" | quads: "}{docPresenceDetail.quadsFound ?? 0}
              {docPresenceDetail.rejectArea > 0 && <>{" | rejArea:"}{docPresenceDetail.rejectArea}</>}
              {docPresenceDetail.rejectAspect > 0 && <>{" | rejAsp:"}{docPresenceDetail.rejectAspect}</>}
              {docPresenceDetail.rejectConvex > 0 && <>{" | rejCvx:"}{docPresenceDetail.rejectConvex}</>}
              {" | "}{docPresenceDetail.frameSize ?? "?"}
            </span>
          )}
          {passportInfo && (
            <span style={{ width: "100%", borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 3, marginTop: 2 }}>
              {passportInfo.isPassport ? "\u2705" : "\u274C"} passport
              {" | "}
              {passportInfo.mrzDetected ? "\u2705" : "\u274C"} mrz (bands:{passportInfo.bandCount} rows:{passportInfo.denseRowCount})
              {" | "}
              {passportInfo.aspectRatioPassport ? "\u2705" : "\u274C"} aspect:{passportInfo.aspectRatio} (1.3-1.5=pp)
            </span>
          )}
        </div>
      )}

      {/* Branding */}
      <div
        style={{
          position: "absolute",
          bottom: isMobile ? 54 : 32,
          left: 0,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          pointerEvents: "none",
          zIndex: 20,
        }}
      >
        <span
          style={{
            color: "rgba(255,255,255,0.85)",
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
          }}
        >
          {locale?.capture?.capturedBy || "Captured by"}
        </span>
        <VeratadLogo style={{ height: 22, width: "auto", opacity: 0.9 }} />
      </div>
    </div>
  );

  return ReactDOM.createPortal(portal, document.body);
}
